
import Vue from 'vue'
import { getCallbotTelephonyBalance, getCallbotTelephonyHistory } from '@/api/callbot'
import { CallbotTelephonyEntry, CallbotTelephonyHistoryTab, CallbotTelephonyPeriod } from '@/types/callbot'
import TelephonyTopupDialog from './TelephonyTopupDialog.vue'
import ZaIcon from './ZaIcon.vue'
import {
    TELEPHONY_PAGE_SIZE,
    TELEPHONY_PERIODS,
    TelephonyWalletState,
    formatOperationDate,
    formatRub,
    formatSignedRub,
    operationCaption,
    operationTitle,
    periodSince,
    walletState,
} from './telephony'

const REFRESH_MS = 30_000

export default Vue.extend({
    name: 'CallbotTelephony',
    components: { TelephonyTopupDialog, ZaIcon },
    props: {
        projectId: { type: String, required: true },
        canManage: { type: Boolean, default: false },
        topupReturned: { type: Boolean, default: false },
    },
    data: () => ({
        balance: null as number | null,
        items: [] as CallbotTelephonyEntry[],
        tab: 'charges' as CallbotTelephonyHistoryTab,
        period: '30' as CallbotTelephonyPeriod,
        pageIndex: 0,
        pageSize: TELEPHONY_PAGE_SIZE,
        loading: false,
        error: '',
        requestId: 0,
        timer: null as number | null,
        topupOpen: false,
        rulesOpen: false,
        paymentOpened: false,
        periods: TELEPHONY_PERIODS,
        tabs: [
            { value: 'charges', label: 'Списания' },
            { value: 'topups', label: 'Пополнения' },
        ] as Array<{ value: CallbotTelephonyHistoryTab; label: string }>,
    }),
    computed: {
        state(): TelephonyWalletState {
            return walletState(this.balance ?? 0)
        },
        page(): CallbotTelephonyEntry[] {
            const start = this.pageIndex * this.pageSize
            return this.items.slice(start, start + this.pageSize)
        },
        pagerCopy(): string {
            const from = this.pageIndex * this.pageSize + 1
            const to = Math.min(this.items.length, from + this.pageSize - 1)
            return `${from}–${to} из ${this.items.length}`
        },
    },
    watch: {
        tab() {
            this.load()
        },
        period() {
            this.load()
        },
        projectId() {
            this.load()
        },
    },
    async mounted() {
        await this.load()
        this.timer = window.setInterval(() => this.load(true), REFRESH_MS)
        document.addEventListener('visibilitychange', this.onVisibilityChange)
    },
    beforeDestroy() {
        if (this.timer !== null) window.clearInterval(this.timer)
        document.removeEventListener('visibilitychange', this.onVisibilityChange)
        this.requestId += 1
    },
    methods: {
        money: formatRub,
        signed: formatSignedRub,
        date: formatOperationDate,
        title: operationTitle,
        caption: operationCaption,
        // Вернулись из вкладки с оплатой — сразу обновляем баланс.
        onVisibilityChange() {
            if (document.visibilityState === 'visible') this.load(true)
        },
        async load(silent = false) {
            const requestId = ++this.requestId
            if (!silent) {
                this.loading = true
                this.pageIndex = 0
            }
            const [balance, history] = await Promise.all([
                getCallbotTelephonyBalance(this.projectId),
                getCallbotTelephonyHistory(this.projectId, this.tab, periodSince(this.period)),
            ])
            if (requestId !== this.requestId) return
            this.loading = false
            if (balance.error || history.error) {
                if (!silent) this.error = 'Не удалось загрузить баланс. Попробуйте обновить страницу.'
                return
            }
            this.error = ''
            this.balance = balance.data?.balanceRub ?? 0
            this.items = history.data?.items ?? []
            this.$emit('balance', this.balance)
        },
    },
})
